/**
 * v2.0.1 minimum Variable set for Phase 7 (Session 10d).
 *
 * The v2.0.1 device model is hierarchical: Variables live on
 * Components, qualified by attribute type (Actual / Target / MinSet /
 * MaxSet). Session 10d ships the bare minimum needed to run a
 * complete charging flow, all on the Actual attribute — see
 * `data/ocpp-adapter.yaml#configuration.v201.keys_implemented_v3_0_phase_7`
 * for the canonical list.
 *
 * Storage shortcut: rather than add a new Dexie table that mirrors
 * `(componentName, variableName, attributeType, value)`, we flatten
 * each entry to `"Component.Variable"` and reuse the existing v16
 * `configurationKeys` table. This is a Phase 7 simplification — the
 * full v2.0.1 device model (multiple attributes per variable,
 * evseId/connectorId qualification, monitoring config) will need its
 * own table when v3.1 lands SecurityCtrlr / ISO15118Ctrlr / etc.
 *
 * GetVariables/SetVariables (inbound CSMS calls) are translated into
 * the v1.6-flavoured `GetConfigurationIncoming` / `ChangeConfigurationIncoming`
 * events by the v201 inbound dispatcher, using the same flat key. The
 * store's existing handler is then version-aware on the OUTBOUND
 * reply only (v201 wraps the response in `getVariableResult: [...]`
 * shape; v16 keeps `configurationKey: [...]`).
 */
import { ConfigurationKeyType, ConfigurationSection } from '@/domain/types';
import type { ConfigurationKeyDescriptor } from '@/protocol/OcppAdapter';

function v(
  component: string,
  variable: string,
  value: string,
  readOnly: boolean,
  type: ConfigurationKeyType,
  section: ConfigurationSection,
): ConfigurationKeyDescriptor {
  return { key: `${component}.${variable}`, value, readonly: readOnly, type, section };
}

/**
 * The 10 default v2.0.1 Variables — keys phrased as
 * `"<Component>.<Variable>"`. See header comment for the
 * architecture-data reference.
 */
export const DEFAULT_CONFIGURATION_KEYS_V201: readonly ConfigurationKeyDescriptor[] = [
  v('OCPPCommCtrlr', 'HeartbeatInterval', '300', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  v('OCPPCommCtrlr', 'WebSocketPingInterval', '0', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  v('OCPPCommCtrlr', 'MessageTimeout', '30', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  // Read-only — computed from topology at runtime, the value here is a
  // sentinel for first-boot display before the live count is available.
  v('DeviceDataCtrlr', 'NumberOfConnectors', '3', true, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  v(
    'SampledDataCtrlr',
    'TxStartedMeasurands',
    'Energy.Active.Import.Register',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  v(
    'SampledDataCtrlr',
    'TxUpdatedMeasurands',
    'Energy.Active.Import.Register,Power.Active.Import,SoC',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  v(
    'SampledDataCtrlr',
    'TxEndedMeasurands',
    'Energy.Active.Import.Register',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  v(
    'SampledDataCtrlr',
    'TxUpdatedInterval',
    '60',
    false,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Core,
  ),
  v(
    'AuthCtrlr',
    'AuthorizeRemoteStart',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  v(
    'AuthCacheCtrlr',
    'Enabled',
    'false',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
];

export const KEY_V201_HEARTBEAT_INTERVAL = 'OCPPCommCtrlr.HeartbeatInterval';
